import { dec, toFixedScale } from '@platform/domain';
import { Package, Search } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import type { NoteBasisLine } from '../../../api/types';
import { useI18n } from '../../../i18n/I18nProvider';
import { UserText } from '../../../ui/Bidi';
import { Button } from '../../../ui/Button';
import { Money } from '../../../ui/Layout';
import { Sheet } from '../../../ui/Sheet';
import styles from '../Sales.module.css';

/** One line a note may still take from a reference: the line and the invoice it came from. */
export interface ReferenceLineOption {
  /** The referenced invoice's number (the origin shown on the row and later on the card). */
  invoiceNumber: string;
  line: NoteBasisLine;
}

/**
 * Block 11, step 3's «أضف بندًا من المرجع» (notes-editor.md §3, AC-N8). A NEW sheet — `ItemPickerSheet`
 * cannot be reused because it carries a create entry AC-N1/AC-N8 forbid. Built on the same `Sheet`
 * + search + count-confirm pattern: a search, then rows in the order item → quantity → unit price →
 * line total, and ONE confirm («أضف {n} بنود إلى الإشعار», RN4R-02). No list/grid toggle, no create
 * entry, and no parenthesised number anywhere. The caller only offers lines not already taken, so a
 * referenced line is never added twice (the domain enforces it; the UI never offers it).
 */
export function ReferenceLinesSheet({
  isOpen,
  onOpenChange,
  options,
  currency,
  onConfirm,
}: {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  /** The reference lines still available to take (already filtered of the taken ones). */
  options: ReferenceLineOption[];
  /** The note's currency (the reference's), for the unit price and line total. */
  currency: string;
  onConfirm: (picked: ReferenceLineOption[]) => void;
}) {
  const { t } = useI18n();
  const [q, setQ] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  useEffect(() => {
    if (isOpen) {
      setQ('');
      setSelected(new Set());
    }
  }, [isOpen]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return options;
    return options.filter((o) => {
      const hay = `${o.line.description} ${o.line.itemCode} ${o.line.internalCode ?? ''} ${
        o.invoiceNumber
      }`.toLowerCase();
      return hay.includes(needle);
    });
  }, [options, q]);

  const byId = useMemo(() => new Map(options.map((o) => [o.line.id, o])), [options]);
  const toggle = (id: string) =>
    setSelected((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  return (
    <Sheet
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      title={t.editor.note.addLine}
      testId="reference-lines"
      footer={
        <div className={styles.sheetFooter}>
          <span />
          <span className={styles.actionStatus} aria-live="polite">
            {t.catalogue.selected(selected.size)}
          </span>
          <Button
            variant="primary"
            isDisabled={selected.size === 0}
            testId="reference-lines-confirm"
            onPress={() => {
              const picked = [...selected].flatMap((id) => {
                const o = byId.get(id);
                return o ? [o] : [];
              });
              onConfirm(picked);
              onOpenChange(false);
            }}
          >
            {t.editor.note.linesToNote(selected.size)}
          </Button>
        </div>
      }
    >
      <div className={styles.formStack}>
        <label className={styles.search}>
          <Search size={16} aria-hidden="true" />
          <span className="visually-hidden">{t.editor.note.addLine}</span>
          <input
            type="search"
            value={q}
            autoFocus
            placeholder={t.editor.note.addLineHint}
            onChange={(e) => setQ(e.target.value)}
            data-testid="reference-lines-search"
          />
        </label>
        <ul className={styles.pickList} aria-label={t.editor.note.addLine}>
          {filtered.map((o) => {
            const { line } = o;
            const on = selected.has(line.id);
            const total = toFixedScale(dec(line.quantity || '0').times(dec(line.unitPrice || '0')));
            return (
              <li key={line.id}>
                <label
                  className={styles.pickRow}
                  data-selected={on}
                  data-testid={`ref-line-${line.id}`}
                >
                  <input type="checkbox" checked={on} onChange={() => toggle(line.id)} />
                  <span>
                    <span className={styles.pickTitle}>
                      <UserText>{line.description}</UserText>
                    </span>
                    <span className={`${styles.subtle} ${styles.pickMeta}`}>
                      {t.editor.note.fromInvoice(o.invoiceNumber)} ·{' '}
                      {t.editor.note.fromLine(line.lineNo)}
                    </span>
                    <span className={`${styles.subtle} ${styles.pickMeta}`}>
                      {t.editor.lines.quantity}: {dec(line.quantity || '0').toString()} ·{' '}
                      {t.editor.lines.unitPrice(currency)}:{' '}
                      <Money value={line.unitPrice} currency={currency} />
                    </span>
                  </span>
                  <Money value={total} currency={currency} testId={`ref-line-total-${line.id}`} />
                </label>
              </li>
            );
          })}
          {filtered.length === 0 ? (
            <li className={styles.emptyLines}>
              <Package size={22} aria-hidden="true" />
              {t.catalogue.empty}
            </li>
          ) : null}
        </ul>
      </div>
    </Sheet>
  );
}
